import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Loader2,
  Search,
  Clock,
  Smartphone,
  AlertCircle,
  CheckCircle2,
  Package,
  Store,
  MapPin,
  History,
  ArrowLeft,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { getStatusColor, getStatusLabel, formatDate } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RepairHistory() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("");

  // Get subdomain from URL or query param
  const getSubdomain = () => {
    const querySubdomain = searchParams.get("shop");
    if (querySubdomain) return querySubdomain;

    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length >= 3 && parts[0] !== "www") {
      return parts[0];
    }
    return null;
  };

  const subdomain = getSubdomain();

  useEffect(() => {
    if (subdomain) {
      fetchShopInfo();
    }
  }, [subdomain]);

  const fetchShopInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/info`);
      setShopInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch shop info:", err);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!phone.trim() || phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    setError(null);
    setHistory(null);

    try {
      const res = await axios.get(
        `${API}/api/public/shop/${subdomain}/history?phone=${encodeURIComponent(phone)}`
      );
      setHistory(res.data);
    } catch (err) {
      console.error("History fetch failed:", err);
      setError(err.response?.data?.detail || "No repair history found for this phone number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="repair-history-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {shopInfo?.logo_url ? (
              <img
                src={shopInfo.logo_url.startsWith("http") ? shopInfo.logo_url : `${API}${shopInfo.logo_url}`.replace("/api", "")}
                alt={shopInfo.shop_name}
                className="w-10 h-10 object-contain rounded-lg"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-lg">{shopInfo?.shop_name || "Repair History"}</h1>
              {shopInfo?.address && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {shopInfo.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Search Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Your Repair History
            </CardTitle>
            <CardDescription>
              Enter your phone number to view all your past repairs at this shop
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your registered phone number"
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full" data-testid="search-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    View History
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History Result */}
        {history && (
          <div className="space-y-4 animate-in">
            {/* Summary */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Repairs at</p>
                    <p className="text-lg font-semibold">{history.shop_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary">{history.total_jobs}</p>
                    <p className="text-sm text-muted-foreground">repairs</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job List */}
            <div className="space-y-3">
              {history.jobs.map((job, index) => (
                <Card key={job.job_number} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        {/* Job Number & Status */}
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{job.job_number}</span>
                          <Badge className={getStatusColor(job.status)}>
                            {getStatusLabel(job.status)}
                          </Badge>
                        </div>

                        {/* Device Info */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Smartphone className="w-4 h-4" />
                          <span>
                            {job.device?.type}
                            {job.device?.brand && ` - ${job.device.brand}`}
                            {job.device?.model && ` ${job.device.model}`}
                          </span>
                        </div>

                        {/* Problem */}
                        <p className="text-sm line-clamp-2">{job.problem_description}</p>

                        {/* Date */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(job.created_at)}</span>
                        </div>
                      </div>

                      {/* Status Icon */}
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          job.status === "delivered" || job.status === "closed"
                            ? "bg-green-100 text-green-600"
                            : job.status === "repaired"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-orange-100 text-orange-600"
                        }`}
                      >
                        {job.status === "delivered" || job.status === "closed" ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <Package className="w-5 h-5" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Back Button */}
            <Button
              variant="outline"
              onClick={() => {
                setHistory(null);
                setPhone("");
              }}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Search Another Number
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
